import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useStorePage, useStoreProducts, storePageQueryOptions } from '@/hooks/storePageHooks';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import ProductCard from '@/components/store/ProductCard';
import StoreProfileCard from '@/components/store/StoreProfileCard';
import BadgesPlaceholder from '@/components/store/BadgesPlaceholder';
// Remove static import: import CataloguesTab from '@/components/store/CataloguesTab';
import { useInView } from 'react-intersection-observer';
import SpecificationFilters from '@/components/store/SpecificationFilters';
import { Search, ShoppingBag, Loader2 } from 'lucide-react';

// Lazy load CataloguesTab – only loaded when the tab is active
const CataloguesTab = lazy(() => import('@/components/store/CataloguesTab'));

export const Route = createFileRoute('/_regular/store/$storeSlug')({
  component: StoreProfile,
  loader: ({ context: { queryClient }, params }) =>
    queryClient
      .ensureQueryData(storePageQueryOptions(params.storeSlug))
      .catch(() => null),
  head: ({ loaderData: store }) => {
    if (!store) return {}
    const description = store.description?.slice(0, 160)
    const image = store.cover_urls?.big || store.cover_urls?.url || store.logo_urls?.big || store.logo_urls?.url
    return {
      meta: [
        { title: `${store.name} | سكيكيمة` },
        description && { name: 'description', content: description },
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: store.name },
        description && { property: 'og:description', content: description },
        image && { property: 'og:image', content: image },
      ].filter(Boolean),
    }
  },
});

function StoreProfile() {
  const { storeSlug } = Route.useParams();
  const { data: store, isLoading: storeLoading } = useStorePage(storeSlug);
  const [activeTab, setActiveTab] = useState('products');
  const [selectedCatalogue, setSelectedCatalogue] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('latest');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [specFilters, setSpecFilters] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const sortMapping = {
    latest: '-created_at',
    price_asc: 'price',
    price_desc: '-price',
  };

  const specifications = store?.specifications || [];

  const filters = useMemo(
    () => ({
      catalogue_id: selectedCatalogue !== 'all' ? selectedCatalogue : undefined,
      search: debouncedSearch || undefined,
      sort: sortMapping[sortBy] || '-created_at',
      spec: specFilters,
    }),
    [selectedCatalogue, debouncedSearch, sortBy, specFilters]
  );

  const {
    data: productsData,
    isLoading: productsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useStoreProducts(store?.slug, filters);

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const products = productsData?.pages?.flatMap((page) => page.data) || [];

  if (storeLoading) {
    return (
      <div className="container mx-auto py-8 px-4 space-y-6">
        <Skeleton className="h-64 w-full rounded-xl" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton className="h-12 w-full" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-64 w-full rounded-lg" />
              ))}
            </div>
          </div>
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (!store) {
    return <div className="container mx-auto py-8 text-center">المتجر غير موجود</div>;
  }

  return (
    <div className="container mx-auto py-6 md:py-8 px-4 space-y-6" dir="rtl">
      {/* Large screens: StoreProfileCard + Badges side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-stretch">
        <div className="lg:col-span-3">
          <StoreProfileCard store={store} />
        </div>
        <div className="lg:col-span-1">
          <BadgesPlaceholder />
        </div>
      </div>

      {/* Full‑width tabs for products and catalogues */}
      <div className="mt-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" dir="rtl">
          <div className="border-b border-border">
            <TabsList className="flex h-auto w-full justify-start gap-6 bg-transparent p-0">
              <TabsTrigger
                value="products"
                className="relative rounded-none px-1 pb-3 text-sm font-medium text-muted-foreground data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:after:absolute data-[state=active]:after:bottom-0 data-[state=active]:after:left-0 data-[state=active]:after:h-0.5 data-[state=active]:after:w-full data-[state=active]:after:bg-brand transition-all"
              >
                المنتجات
              </TabsTrigger>
              <TabsTrigger
                value="catalogues"
                className="relative rounded-none px-1 pb-3 text-sm font-medium text-muted-foreground data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:after:absolute data-[state=active]:after:bottom-0 data-[state=active]:after:left-0 data-[state=active]:after:h-0.5 data-[state=active]:after:w-full data-[state=active]:after:bg-brand transition-all"
              >
                الكتالوجات
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Products Tab – unchanged */}
          <TabsContent value="products" className="pt-6 space-y-6">
            {/* Filters row */}
            <div className="flex flex-wrap items-center gap-3 bg-muted/30 rounded-lg p-3">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="بحث في المنتجات..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-9 bg-background"
                />
              </div>
              <Select className="w-full" value={selectedCatalogue} onValueChange={setSelectedCatalogue}>
                <SelectTrigger className="w-[180px] bg-background flex-row-reverse justify-between gap-2 [&>span]:text-right">
                  <SelectValue placeholder="كل الكتالوجات" />
                </SelectTrigger>
                <SelectContent className="w-fit">
                  <SelectItem value="all">كل الكتالوجات</SelectItem>
                  {store.catalogues?.map((cat) => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px] bg-background">
                  <SelectValue placeholder="ترتيب حسب" />
                </SelectTrigger>
                <SelectContent className="w-fit" dir="rtl">
                  <SelectItem value="latest">الأحدث</SelectItem>
                  <SelectItem value="price_asc">السعر: من الأقل إلى الأعلى</SelectItem>
                  <SelectItem value="price_desc">السعر: من الأعلى إلى الأقل</SelectItem>
                </SelectContent>
              </Select>
              {specifications.length > 0 && (
                <SpecificationFilters
                  specifications={specifications}
                  onFilterChange={setSpecFilters}
                />
              )}
            </div>

            {/* Products Grid */}
            {productsLoading && products.length === 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {[...Array(10)].map((_, i) => (
                  <Skeleton key={i} className="aspect-square w-full rounded-lg" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                <ShoppingBag className="h-12 w-12 mb-3 opacity-50" />
                <p>لا توجد منتجات تطابق بحثك</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
                {hasNextPage && (
                  <div className="flex justify-center pt-4">
                    <Button
                      variant="outline"
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className="min-w-[180px]"
                    >
                      {isFetchingNextPage ? (
                        <>
                          <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                          جاري التحميل...
                        </>
                      ) : (
                        'تحميل المزيد'
                      )}
                    </Button>
                  </div>
                )}
                <div ref={ref} className="h-10" />
              </>
            )}
          </TabsContent>

          {/* Catalogues Tab – now lazy‑loaded with Suspense */}
          <TabsContent value="catalogues" className="pt-6">
            <Suspense fallback={
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            }>
              <CataloguesTab catalogues={store.catalogues} />
            </Suspense>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}